// src/components/CustomAccordion.jsx
import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';

const CustomAccordion = ({ 
  title, 
  children, 
  defaultExpanded = false,
  headerComponent = null,
  variant = 'default',
  titleComponent = null,
  showExpandIcon = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Determine colors based on variant
  const getColors = () => {
    if (variant === 'primary') {
      return {
        bgExpanded: theme.palette.primary.main,
        textExpanded: theme.palette.primary.contrastText,
        bgHover: '#e3f2fd', // Lightest blue for primary
        bgNormal: theme.palette.background.paper
      };
    } else if (variant === 'secondary') {
      return {
        bgExpanded: theme.palette.secondary.main,
        textExpanded: theme.palette.secondary.contrastText,
        bgHover: '#fce4ec', // Lightest pink for secondary
        bgNormal: theme.palette.background.paper
      };
    } else {
      return {
        bgExpanded: theme.palette.primary.main,
        textExpanded: theme.palette.primary.contrastText,
        bgHover: '#f5f5f5', // Light grey for default
        bgNormal: theme.palette.background.paper
      };
    }
  };

  const colors = getColors();

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: expanded ? theme.shadows[3] : theme.shadows[1],
        transition: theme.transitions.create(['box-shadow', 'margin'], {
          duration: theme.transitions.duration.short,
        }),
        '&:before': {
          display: 'none', // Remove the default divider
        },
        '&:hover': {
          backgroundColor: expanded ? 'transparent' : colors.bgHover,
        },
        '& .MuiAccordionSummary-root': {
          backgroundColor: colors.bgNormal,
          color: theme.palette.text.primary,
          minHeight: isMobile ? 48 : 56,
          padding: isMobile ? theme.spacing(0, 1.25) : theme.spacing(0, 2),
          borderBottom: expanded ? '1px solid ' + theme.palette.divider : 'none',
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
          color: theme.palette.text.secondary,
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
          }),
        },
        '& .MuiAccordionSummary-content': {
          margin: isMobile ? theme.spacing(1, 0) : theme.spacing(1.25, 0),
        },
        '& .MuiAccordionDetails-root': {
          padding: isMobile ? theme.spacing(1) : theme.spacing(2),
          backgroundColor: theme.palette.background.paper,
          overflowX: 'clip',
        },
      }}
    >
      <AccordionSummary 
        expandIcon={showExpandIcon ? <ExpandMoreIcon /> : null}
        id={`accordion-header-${title?.toString().replace(/\s+/g, '-').toLowerCase() || 'panel'}`}
      >
        {headerComponent ? (
          headerComponent
        ) : (
          <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {titleComponent ? (
              titleComponent
            ) : (
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"}
                sx={{
                  fontWeight: 600,
                  fontSize: isMobile ? '0.95rem' : '1.05rem',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {title}
              </Typography>
            )}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

CustomAccordion.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool,
  headerComponent: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'primary', 'secondary']),
  titleComponent: PropTypes.node,
  showExpandIcon: PropTypes.bool,
};

export default CustomAccordion;
